import { useEffect, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Building,
  Loader2,
  MapPin,
  Truck,
  User as UserIcon,
  Users,
  X,
} from 'lucide-react';
import type { AffectationType } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panel timeline des affectations + mouvements d'un équipement (US-EQ-06 / 07).
 *
 *  - Fusion AffectationEquipement + MouvementEquipement triés par date desc.
 *  - Badges colorés par type (service/utilisateur/localisation/mouvement/
 *    acceptation/refus).
 *  - Actions : affecter à un service, demander à un utilisateur, créer un
 *    mouvement de localisation.
 */

interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}
interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
}
interface BatimentOption {
  id: string;
  code: string;
  nom: string;
}
interface EtageOption {
  id: string;
  code: string;
  numero: string;
  nom: string | null;
}
interface SalleOption {
  id: string;
  code: string;
  nom: string;
}

interface AffectationRow {
  id: string;
  type_affectation: AffectationType;
  motif: string | null;
  commentaire: string | null;
  motif_refus: string | null;
  statut_action: string | null;
  date_demande: string | null;
  date_reponse: string | null;
  created_at: string;
  ancien_service: { id: string; nom_complet: string } | null;
  nouveau_service: { id: string; nom_complet: string } | null;
  ancien_utilisateur: { id: string; first_name: string; last_name: string } | null;
  nouveau_utilisateur: { id: string; first_name: string; last_name: string } | null;
  ancien_batiment: { id: string; nom: string } | null;
  nouveau_batiment: { id: string; nom: string } | null;
  ancien_etage: { id: string; numero: string } | null;
  nouveau_etage: { id: string; numero: string } | null;
  ancien_salle: { id: string; nom: string } | null;
  nouveau_salle: { id: string; nom: string } | null;
  creator: { id: string; first_name: string; last_name: string } | null;
}

interface MouvementRow {
  id: string;
  date_mouvement: string;
  motif: string | null;
  observations: string | null;
  origine_batiment: { id: string; nom: string } | null;
  origine_etage: { id: string; numero: string } | null;
  origine_salle: { id: string; nom: string } | null;
  destination_batiment: { id: string; nom: string } | null;
  destination_etage: { id: string; numero: string } | null;
  destination_salle: { id: string; nom: string } | null;
  demandeur: { id: string; first_name: string; last_name: string } | null;
}

type TimelineItem =
  | { kind: 'affectation'; date: string; payload: AffectationRow }
  | { kind: 'mouvement'; date: string; payload: MouvementRow };

export function AffectationsPanel({
  equipementId,
  onRefresh,
}: {
  equipementId: string;
  onRefresh?(): void;
}) {
  const { can } = usePermissions();
  const canEdit = can('equipements', 'update');

  const [affectations, setAffectations] = useState<AffectationRow[]>([]);
  const [mouvements, setMouvements] = useState<MouvementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [serviceModal, setServiceModal] = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [mouvementModal, setMouvementModal] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        api.get<{ data: AffectationRow[] }>(`/equipements/${equipementId}/affectations`),
        api.get<{ data: MouvementRow[] }>(`/equipements/${equipementId}/mouvements`),
      ]);
      setAffectations(a.data.data);
      setMouvements(m.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipementId]);

  const timeline: TimelineItem[] = [
    ...affectations.map<TimelineItem>((a) => ({
      kind: 'affectation',
      date: a.created_at,
      payload: a,
    })),
    ...mouvements.map<TimelineItem>((m) => ({
      kind: 'mouvement',
      date: m.date_mouvement,
      payload: m,
    })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  function onSaved() {
    setServiceModal(false);
    setUserModal(false);
    setMouvementModal(false);
    refresh();
    onRefresh?.();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={() => setServiceModal(true)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <Building className="w-3.5 h-3.5 mr-1" />
            Affecter à un service
          </button>
          <button
            onClick={() => setUserModal(true)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <Users className="w-3.5 h-3.5 mr-1" />
            Demander à un utilisateur
          </button>
          <button
            onClick={() => setMouvementModal(true)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <Truck className="w-3.5 h-3.5 mr-1" />
            Nouveau mouvement
          </button>
        </div>
      )}

      {timeline.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aucune affectation enregistrée.</p>
      ) : (
        <ol className="relative border-l border-gray-200 ml-3 space-y-3">
          {timeline.map((item) => (
            <li key={`${item.kind}-${item.payload.id}`} className="ml-5">
              <span className="absolute -left-2.5 flex items-center justify-center w-5 h-5 rounded-full ring-4 ring-white bg-gray-100">
                <TimelineIcon item={item} />
              </span>
              <div className="rounded-lg border border-gray-200 p-2.5 bg-white">
                {item.kind === 'affectation' ? (
                  <AffectationLine row={item.payload} />
                ) : (
                  <MouvementLine row={item.payload} />
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(item.date).toLocaleString()}
                  {item.kind === 'affectation' && item.payload.creator && (
                    <span className="ml-2">
                      par {item.payload.creator.first_name} {item.payload.creator.last_name}
                    </span>
                  )}
                  {item.kind === 'mouvement' && item.payload.demandeur && (
                    <span className="ml-2">
                      par {item.payload.demandeur.first_name} {item.payload.demandeur.last_name}
                    </span>
                  )}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}

      {serviceModal && (
        <AffecterServiceModal
          equipementId={equipementId}
          onClose={() => setServiceModal(false)}
          onSaved={onSaved}
        />
      )}
      {userModal && (
        <DemanderUtilisateurModal
          equipementId={equipementId}
          onClose={() => setUserModal(false)}
          onSaved={onSaved}
        />
      )}
      {mouvementModal && (
        <NouveauMouvementModal
          equipementId={equipementId}
          onClose={() => setMouvementModal(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function TimelineIcon({ item }: { item: TimelineItem }) {
  if (item.kind === 'mouvement') return <Truck className="w-3 h-3 text-teal-700" />;
  const t = item.payload.type_affectation;
  if (t === 'service') return <Building className="w-3 h-3 text-blue-700" />;
  if (t === 'utilisateur') return <UserIcon className="w-3 h-3 text-purple-700" />;
  if (t === 'localisation') return <MapPin className="w-3 h-3 text-teal-700" />;
  if (t === 'acceptation') return <UserIcon className="w-3 h-3 text-green-700" />;
  if (t === 'refus') return <UserIcon className="w-3 h-3 text-red-700" />;
  return <UserIcon className="w-3 h-3 text-gray-500" />;
}

function AffectationLine({ row }: { row: AffectationRow }) {
  const t = row.type_affectation;
  const badge = TYPE_BADGE[t];
  return (
    <div className="text-sm">
      <p className="flex items-center flex-wrap gap-2">
        <span
          className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium border ${badge.cls}`}
        >
          {badge.label}
        </span>
        {t === 'service' && (
          <span className="text-gray-700 text-xs">
            {row.ancien_service?.nom_complet ?? '∅'}{' '}
            <ArrowRight className="w-3 h-3 inline mx-0.5" />{' '}
            {row.nouveau_service?.nom_complet ?? '∅'}
          </span>
        )}
        {t === 'utilisateur' && (
          <span className="text-gray-700 text-xs">
            {row.ancien_utilisateur
              ? `${row.ancien_utilisateur.first_name} ${row.ancien_utilisateur.last_name}`
              : '∅'}{' '}
            <ArrowRight className="w-3 h-3 inline mx-0.5" />{' '}
            {row.nouveau_utilisateur
              ? `${row.nouveau_utilisateur.first_name} ${row.nouveau_utilisateur.last_name}`
              : '∅'}
          </span>
        )}
        {t === 'localisation' && (
          <span className="text-gray-700 text-xs">
            {formatLoc(row.ancien_batiment?.nom, row.ancien_etage?.numero, row.ancien_salle?.nom)}{' '}
            <ArrowRight className="w-3 h-3 inline mx-0.5" />{' '}
            {formatLoc(
              row.nouveau_batiment?.nom,
              row.nouveau_etage?.numero,
              row.nouveau_salle?.nom,
            )}
          </span>
        )}
        {(t === 'acceptation' || t === 'refus') && row.nouveau_utilisateur && (
          <span className="text-gray-700 text-xs">
            par {row.nouveau_utilisateur.first_name} {row.nouveau_utilisateur.last_name}
          </span>
        )}
      </p>
      {row.motif && <p className="text-xs text-gray-600 italic mt-1">"{row.motif}"</p>}
      {row.motif_refus && (
        <p className="text-xs text-red-700 mt-1">Motif de refus : {row.motif_refus}</p>
      )}
      {row.commentaire && <p className="text-xs text-gray-600 mt-1">{row.commentaire}</p>}
    </div>
  );
}

function MouvementLine({ row }: { row: MouvementRow }) {
  return (
    <div className="text-sm">
      <p className="flex items-center flex-wrap gap-2">
        <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium border bg-teal-100 text-teal-800 border-teal-200">
          Mouvement
        </span>
        <span className="text-gray-700 text-xs">
          {formatLoc(row.origine_batiment?.nom, row.origine_etage?.numero, row.origine_salle?.nom)}{' '}
          <ArrowRight className="w-3 h-3 inline mx-0.5" />{' '}
          {formatLoc(
            row.destination_batiment?.nom,
            row.destination_etage?.numero,
            row.destination_salle?.nom,
          )}
        </span>
      </p>
      {row.motif && <p className="text-xs text-gray-600 italic mt-1">"{row.motif}"</p>}
      {row.observations && <p className="text-xs text-gray-600 mt-1">{row.observations}</p>}
    </div>
  );
}

function formatLoc(batiment?: string, etage?: string, salle?: string): string {
  const parts = [batiment, etage ? `Étage ${etage}` : null, salle].filter(Boolean);
  return parts.length > 0 ? parts.join(' › ') : '∅';
}

const TYPE_BADGE: Record<AffectationType, { label: string; cls: string }> = {
  service: { label: 'Service', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  utilisateur: {
    label: 'Utilisateur',
    cls: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  desaffectation: {
    label: 'Désaffectation',
    cls: 'bg-gray-100 text-gray-700 border-gray-200',
  },
  localisation: {
    label: 'Localisation',
    cls: 'bg-teal-100 text-teal-800 border-teal-200',
  },
  acceptation: {
    label: 'Acceptation',
    cls: 'bg-green-100 text-green-800 border-green-200',
  },
  refus: { label: 'Refus', cls: 'bg-red-100 text-red-800 border-red-200' },
};

// ─── Mini-modals d'action ──────────────────────────────────────────────────

function AffecterServiceModal({
  equipementId,
  onClose,
  onSaved,
}: {
  equipementId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [serviceId, setServiceId] = useState('');
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/equipements/${equipementId}/affecter-service`, {
        service_id: serviceId || null,
        motif,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <MiniModal title="Affecter à un service" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3 p-4">
        {errorMessage && <ErrorBanner message={errorMessage} />}
        <Field label="Service (vide = désaffecter)">
          <select
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            className={inputClass}
          >
            <option value="">— Désaffecter —</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.nom_complet}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Motif *">
          <textarea
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            rows={3}
            className={inputClass}
            required
          />
        </Field>
        <ActionFooter onClose={onClose} saving={saving} label="Affecter" />
      </form>
    </MiniModal>
  );
}

function DemanderUtilisateurModal({
  equipementId,
  onClose,
  onSaved,
}: {
  equipementId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [personnels, setPersonnels] = useState<PersonnelOption[]>([]);
  const [userId, setUserId] = useState('');
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setPersonnels(res.data.data))
      .catch(() => setPersonnels([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!userId) {
      setErrorMessage('Sélectionnez un utilisateur.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/equipements/${equipementId}/demander-affectation`, {
        utilisateur_id: userId,
        motif,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <MiniModal title="Demander affectation à un utilisateur" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3 p-4">
        {errorMessage && <ErrorBanner message={errorMessage} />}
        <Field label="Utilisateur *">
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className={inputClass}
            required
          >
            <option value="">— Sélectionnez —</option>
            {personnels.map((p) => (
              <option key={p.id} value={p.id}>
                {p.last_name} {p.first_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Motif *">
          <textarea
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            rows={3}
            className={inputClass}
            required
          />
        </Field>
        <p className="text-xs text-gray-500 italic">
          L'utilisateur recevra une notification et pourra accepter ou refuser depuis sa page "Mes
          affectations".
        </p>
        <ActionFooter onClose={onClose} saving={saving} label="Envoyer" />
      </form>
    </MiniModal>
  );
}

function NouveauMouvementModal({
  equipementId,
  onClose,
  onSaved,
}: {
  equipementId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [batiments, setBatiments] = useState<BatimentOption[]>([]);
  const [etages, setEtages] = useState<EtageOption[]>([]);
  const [salles, setSalles] = useState<SalleOption[]>([]);

  const [batimentId, setBatimentId] = useState('');
  const [etageId, setEtageId] = useState('');
  const [salleId, setSalleId] = useState('');
  const [motif, setMotif] = useState('');
  const [observations, setObservations] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: BatimentOption[] }>('/batiments?page_size=200')
      .then((res) => setBatiments(res.data.data))
      .catch(() => setBatiments([]));
  }, []);

  useEffect(() => {
    if (!batimentId) {
      setEtages([]);
      setEtageId('');
      return;
    }
    api
      .get<{ data: EtageOption[] }>(`/etages?batiment_id=${batimentId}`)
      .then((res) => setEtages(res.data.data))
      .catch(() => setEtages([]));
  }, [batimentId]);

  useEffect(() => {
    if (!etageId) {
      setSalles([]);
      setSalleId('');
      return;
    }
    api
      .get<{ data: SalleOption[] }>(`/salles?etage_id=${etageId}&page_size=200`)
      .then((res) => setSalles(res.data.data))
      .catch(() => setSalles([]));
  }, [etageId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/equipements/${equipementId}/mouvements`, {
        destination_batiment_id: batimentId || null,
        destination_etage_id: etageId || null,
        destination_salle_id: salleId || null,
        motif: motif || null,
        observations: observations || null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <MiniModal title="Nouveau mouvement (changement de localisation)" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3 p-4">
        {errorMessage && <ErrorBanner message={errorMessage} />}
        <Field label="Bâtiment de destination">
          <select
            value={batimentId}
            onChange={(e) => setBatimentId(e.target.value)}
            className={inputClass}
          >
            <option value="">— Aucun —</option>
            {batiments.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.nom}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Étage de destination">
          <select
            value={etageId}
            onChange={(e) => setEtageId(e.target.value)}
            className={inputClass}
            disabled={!batimentId}
          >
            <option value="">— Aucun —</option>
            {etages.map((e) => (
              <option key={e.id} value={e.id}>
                Étage {e.numero}
                {e.nom ? ` — ${e.nom}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Salle de destination">
          <select
            value={salleId}
            onChange={(e) => setSalleId(e.target.value)}
            className={inputClass}
            disabled={!etageId}
          >
            <option value="">— Aucune —</option>
            {salles.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.nom}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Motif">
          <input value={motif} onChange={(e) => setMotif(e.target.value)} className={inputClass} />
        </Field>
        <Field label="Observations">
          <textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={3}
            className={inputClass}
          />
        </Field>
        <ActionFooter onClose={onClose} saving={saving} label="Déplacer" />
      </form>
    </MiniModal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function MiniModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose(): void;
  children: React.ReactNode;
}) {
  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">{children}</div>
        </div>
      </div>
    </ModalPortal>
  );
}

function ActionFooter({
  onClose,
  saving,
  label,
}: {
  onClose(): void;
  saving: boolean;
  label: string;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
      >
        Annuler
      </button>
      <button
        type="submit"
        disabled={saving}
        className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
      >
        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {label}
      </button>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-red-700">{message}</p>
    </div>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
